import { Fragment, useState, useRef, useEffect, useLayoutEffect } from "react";
import type { ChangeEvent, ReactNode } from "react";

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import splitPinned from "components/Screens/Marketplace/Customer/TalentNetwork/pinnedOptions";
import "./FilterDropdown.scss";

interface FilterDropdownProps {
  label: string;
  title: string;
  /** A line under the title saying what the filter is for. */
  helper?: ReactNode;
  /**
   * checkbox: each tick applies at once and the panel stays open. single: plain rows, no box or circle; a click
   * picks that one (replacing any other), applies and closes; clicking the picked one again clears it.
   */
  mode: "checkbox" | "single";
  selectedValues: string[];
  // eslint-disable-next-line no-unused-vars
  onApply: (values: string[]) => void;
  /** Options; a description renders under the label. */
  options?: { value: string; label: string; description?: string; group?: string }[];
  /** Top-pinned options shown above divider (e.g. English, Spanish) */
  topOptions?: { value: string; label: string; description?: string; group?: string }[];
  /** Option values also listed above the divider (the customer's onboarding apps). */
  pinned?: string[];
  /** Show search input */
  searchable?: boolean;
  /** Shown when the search matches nothing (prototype .fpop-empty) */
  emptyText?: string;
  /** Wider panel, for options with descriptions */
  wide?: boolean;
  /** A line at the bottom of the panel */
  note?: ReactNode;
}

/** Keep this much space between an open panel and the window's edge. */
const VIEWPORT_GAP = 8;

function FilterDropdown({
  label,
  title,
  helper,
  mode,
  selectedValues,
  onApply,
  options = [],
  topOptions = [],
  pinned = [],
  searchable = true,
  emptyText = "No matches",
  wide = false,
  note,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // What was ticked when the list opened joins the top section; a tick while it is open moves nothing under the
  // cursor, it moves up the next time the list opens.
  const [openPicks, setOpenPicks] = useState<string[]>([]);
  // Opens to the left of its button when there is no room on the right (QA PO-9).
  const [alignRight, setAlignRight] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Measured before paint, so the panel never flashes off the edge of the window.
  useLayoutEffect(() => {
    if (!isOpen) {
      setAlignRight(false);
      return;
    }
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger) return;
    const { left } = trigger.getBoundingClientRect();
    const fitsRight = left + panel.offsetWidth <= document.documentElement.clientWidth - VIEWPORT_GAP;
    setAlignRight(!fitsRight);
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleToggle = () => {
    if (!isOpen) {
      setSearchTerm("");
      setOpenPicks(selectedValues);
    }
    setIsOpen(!isOpen);
  };

  // No Apply button: a pick applies at once (product, 2026-10-22). One "Clear filters" on the page clears them all.
  const handleSelect = (value: string) => {
    if (mode === "single") {
      onApply(selectedValues.includes(value) ? [] : [value]);
      close();
      return;
    }
    onApply(selectedValues.includes(value) ? selectedValues.filter((v) => v !== value) : [...selectedValues, value]);
  };

  const optionText = (opt: { label: string; description?: string }) =>
    opt.description ? (
      <span className="filter-dropdown__option-text">
        <span className="filter-dropdown__option-label">{opt.label}</span>
        <span className="filter-dropdown__option-desc">{opt.description}</span>
      </span>
    ) : (
      <span className="filter-dropdown__option-label">{opt.label}</span>
    );

  const renderOption = (opt: { value: string; label: string; description?: string }, key: string) => {
    const on = selectedValues.includes(opt.value);
    if (mode === "single") {
      return (
        <button
          key={key}
          type="button"
          className={`filter-dropdown__option filter-dropdown__option--single${on ? " filter-dropdown__option--on" : ""}`}
          aria-pressed={on}
          onClick={() => handleSelect(opt.value)}
        >
          {optionText(opt)}
        </button>
      );
    }
    return (
      // eslint-disable-next-line jsx-a11y/label-has-associated-control
      <label key={key} className="filter-dropdown__option">
        <input
          type="checkbox"
          checked={on}
          onChange={() => handleSelect(opt.value)}
          className="filter-dropdown__input filter-dropdown__input--checkbox"
        />
        {optionText(opt)}
      </label>
    );
  };

  const filterBySearch = <T extends { value: string; label: string }>(items: T[]): T[] => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(term));
  };

  const count = selectedValues.length;
  // Single select: the picked option's name on the button instead of a count, which could only ever be 1.
  const picked = mode === "single" && count ? [...topOptions, ...options].find((o) => o.value === selectedValues[0]) : undefined;
  const { top, rest } =
    mode === "checkbox" ? splitPinned(options, [...pinned, ...openPicks], topOptions) : { top: topOptions, rest: options };
  const shownTop = filterBySearch(top);
  const shownOptions = filterBySearch(rest);

  return (
    <div className="filter-bar__section-wrapper">
      <button type="button" ref={triggerRef} className="filter-bar__section" onClick={handleToggle}>
        <span className="filter-bar__label">
          {picked ? (
            <span className="filter-bar__picked">{picked.label}</span>
          ) : (
            <>
              {label}
              {count > 0 && <span className="filter-bar__count"> ({count})</span>}
            </>
          )}
        </span>
        <span className="filter-bar__right">
          <ExpandMoreIcon
            sx={{ fontSize: 24 }}
            className={`filter-bar__chevron ${isOpen ? "filter-bar__chevron--open" : ""}`}
          />
        </span>
      </button>
      {isOpen && (
        <div
          className={`filter-dropdown__panel${wide ? " filter-dropdown__panel--wide" : ""}${alignRight ? " filter-dropdown__panel--right" : ""}`}
          ref={panelRef}
        >
          <div className="filter-dropdown__panel-header">
            <div className="filter-dropdown__title-row">
              <h3 className="filter-dropdown__panel-title">{title}</h3>
              {/* Clears this filter only; there when something is picked */}
              {count > 0 && (
                <button type="button" className="filter-dropdown__clear" onClick={() => onApply([])}>
                  Clear
                </button>
              )}
            </div>
            {helper && <p className="filter-dropdown__helper">{helper}</p>}
          </div>

          {searchable && (
            <div className="filter-dropdown__search">
              <SearchIcon sx={{ fontSize: 20 }} className="filter-dropdown__search-icon" />
              <input
                type="text"
                className="filter-dropdown__search-input"
                placeholder="Search"
                value={searchTerm}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              />
            </div>
          )}

          <div className="filter-dropdown__content">
            {/* Top pinned options (English and Spanish, the onboarding apps, and what is ticked) */}
            {shownTop.length > 0 && (
              <>
                {shownTop.map((opt) => renderOption(opt, `top-${opt.value}`))}
                <div className="filter-dropdown__divider" />
              </>
            )}

            {shownOptions.map((opt, i) => (
                <Fragment key={opt.value}>
                {/* A heading where the group changes (Role: Medical, Dental, ...). */}
                {opt.group && opt.group !== shownOptions[i - 1]?.group && (
                  <div className="filter-dropdown__group">{opt.group}</div>
                )}
                {renderOption(opt, opt.value)}
                </Fragment>
              ))}
            {searchTerm && shownTop.length === 0 && shownOptions.length === 0 && (
              <div className="filter-dropdown__empty">{emptyText}</div>
            )}
          </div>

          {note && <div className="filter-dropdown__note">{note}</div>}

        </div>
      )}
    </div>
  );
}

export default FilterDropdown;
